import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { streamingDataSlice } from '@src/providers/AppStateProvider/streamingDataSlice';
import type { AppExtraArgument, AppState, AppStateListenerStart } from '@src/providers/AppStateProvider/types';
import {
  type Allocation,
  AllocationValueTypeEnum,
  BaseField,
  type CareOrder,
  EMPTY_ARRAY,
  EMPTY_SUB_ACCOUNT_SELECTION,
  Field,
  getExpirationByMarketByCurrencyIdentity,
  getGroup,
  getScaleFromIncrement,
  getStrikesByOptionKey,
  getTypedKeys,
  isOrder,
  type MarketSecurityStatus,
  ModeEnum,
  NumericField,
  Order,
  OrderMarketStatusEnum,
  PricingModeEnum,
  type ProductGroup,
  type Quote,
  RFQTradingProtocolEnum,
  type Security,
  SecurityTypesUtils,
  SelectorField,
  SideEnum,
  toBigWithDefault,
  Unit,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { compact } from 'lodash-es';
import { getRFQMarketSelectorItems } from '../../MarketSelector/RFQ/getRFQMarketSelectorItems';
import type { MarketSelectorItem, MarketSelectorValue } from '../../MarketSelector/types';
import { OrderMarketsField } from '../../MarketSelector/utils/OrderMarketsField';
import { UNITS_CURRENCY } from '../CareOrderFormView/types';
import { getInitMarketsThunk, getMarketSelectorItemsListener } from '../Common/marketSelectionSliceUtils';
import { Option, type OptionModelReferenceData } from '../NewOrder/models';
import { initialRefDataState } from '../OMSReferenceDataSlice';
import { OMSView } from '../OMSView';
import {
  disableAllocationFields,
  mapAllocationsToField,
  touchAllocations,
  updateSubAccountOrAllocationState,
  validateAllocations,
} from '../allocationUtils';
import { numberIsPositive } from '../commonFieldRules';
import type { OMSReferenceDataState } from '../types';
import { careOrderQuantityValidation, quantityValidation } from './FieldRules';
import { FixingField } from './FixingField';
import { careOrderCurrencyToRFQSliceCurrency, getRFQFormCurrencySpec } from './currencies';
import { rfqSelectors } from './selectors';
import type { PrimeRFQParams, RFQCurrency, RFQDependencies, RFQParentType, RFQState } from './types';
import {
  isFalconXOption,
  RFQSLICE_POSITION_REQUEST_PARAMS,
  securityHasSizeCounteringFeatureFlagEnabled,
} from './utils';

export const getInitialState = (): RFQState => ({
  referenceData: initialRefDataState,
  dependencies: {
    tradableSubAccounts: EMPTY_ARRAY,
  },
  form: {
    fundField: new Field<string>({ name: 'Fund', isRequired: false }),
    symbolField: new Field<Security>({ name: 'Symbol' }),
    fixingField: new FixingField(),
    sideField: new Field({ name: 'Side', isRequired: false }),
    quantityField: new NumericField({ name: 'Quantity' }),
    rfqCurrencyField: new SelectorField(),
    rfqMarketsField: new OrderMarketsField({ name: 'Market Account' }),
    groupField: new Field({ name: 'Group', isRequired: false }),
    allocationValueTypeField: new Field({ value: AllocationValueTypeEnum.Percentage }),
    allocations: EMPTY_ARRAY,
    parentIDField: new Field({ name: 'Parent ID', isRequired: false }),
    parentTypeField: new Field({ name: 'Parent Type', isRequired: false }),
    assignToParentField: new Field({ name: 'Assign to Parent', isRequired: false }),
    pricingModeField: new Field({ name: 'Pricing Mode', isRequired: false }),
    rfqTradingProtocolField: new Field({
      name: 'RFQ Protocol',
      isRequired: false,
      value: 'Streaming',
    }),
    externalIDField: new Field({ name: 'External ID', isRequired: false, isDisabled: true }), // externalID is always disabled in RFQ
    externalCommentsField: new Field({ name: 'External Comments', isRequired: false, isDisabled: true }),
    settleDateField: new Field({ name: 'Settlement Date', isRequired: false, isDisabled: true }), // settleDate is always disabled in RFQ
    splitRFQs: false,
    sizeCountering: false,
    partialFills: false,
    option: undefined,
  },
  productGroup: 'Spot',
  isLoading: false,
  initialized: false,
  quoteRequested: false,
  currentQuote: undefined,
  currentCareOrder: undefined,
});

function handlePricingModeChange(state: WritableDraft<RFQState>, pricingMode: PricingModeEnum | undefined) {
  state.form.pricingModeField = state.form.pricingModeField.updateValue(pricingMode);
  state.form.fixingField = state.form.fixingField.setIsRequired(
    state.form.pricingModeField.isVisible && pricingMode === PricingModeEnum.SpreadToFixing
  );
}

const initializeFieldsFromOrder = (state: WritableDraft<RFQState>, order: Order) => {
  handleSymbolChange(state, order.Symbol);

  state.form.sideField = state.form.sideField.updateValue(order.Side === SideEnum.Buy ? SideEnum.Buy : SideEnum.Sell);
  state.form.quantityField = state.form.quantityField.updateValue(
    order.OrderQty && toBigWithDefault(order.OrderQty, 0).toFixed()
  );

  if (order.Currency) {
    state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(order.Currency);
  }

  const rfqTradingProtocol = getRFQTradingProtocolFromEntity(state, order);
  updateRFQTradingProtocol(state, rfqTradingProtocol);
  handlePricingModeChange(state, order.PricingMode);
  updateSubAccountOrAllocationState(state, order);
  state.form.groupField = state.form.groupField.updateValue(order.Group);

  state.form.parentIDField = state.form.parentIDField.updateValue(order.ParentOrderID);
  state.form.parentTypeField = state.form.parentTypeField.updateValue(order.ParentOrderID ? 'care-order' : undefined);
  state.form.assignToParentField = state.form.assignToParentField.updateValue(order.ParentOrderID != null);
  if (order.ParentOrderID != null) {
    state.form.fundField = state.form.fundField.setDisabled(true);
    state.form.symbolField = state.form.symbolField.setDisabled(true);
    state.form.sideField = state.form.sideField.setDisabled(true);
  }

  if (order.FixingDetails && order.PricingMode === PricingModeEnum.SpreadToFixing) {
    state.form.fixingField = FixingField.createFromQuote(order).setIsRequired(true);
  }
  if (order.SettleDate) {
    state.form.settleDateField = state.form.settleDateField.updateValue(order.SettleDate);
  }

  validate(state);
};

const initializeFieldsFromQuote = (state: WritableDraft<RFQState>, quote: Quote) => {
  handleSymbolChange(state, quote.Symbol);

  state.form.sideField = state.form.sideField.updateValue(quote.Side);
  state.form.quantityField = state.form.quantityField.updateValue(
    quote.OrderQty && toBigWithDefault(quote.OrderQty, 0).toFixed()
  );

  if (quote.Currency) {
    state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(quote.Currency);
  }

  const rfqTradingProtocol = getRFQTradingProtocolFromEntity(state, quote);
  updateRFQTradingProtocol(state, rfqTradingProtocol);
  handlePricingModeChange(state, quote.PricingMode);
  updateSubAccountOrAllocationState(state, quote);
  state.form.sizeCountering = quote.NegotiationParameters?.SizeCountering === ModeEnum.Enabled;
  state.form.groupField = state.form.groupField.updateValue(quote.Group);

  state.form.parentIDField = state.form.parentIDField.updateValue(quote.ParentRFQID);
  // TODO, the backend needs to be kinder here. I either need Quotes and Orders to contain a ParentType as well as ParentID,
  // or we need to switch to WorkingOrders only. TODO as a part of the Netting work. Leaving it as this is fine during dev.
  state.form.parentTypeField = state.form.parentTypeField.updateValue(quote.ParentRFQID ? 'care-order' : undefined);
  state.form.assignToParentField = state.form.assignToParentField.updateValue(quote.ParentRFQID != null);
  if (quote.ParentRFQID != null) {
    state.form.fundField = state.form.fundField.setDisabled(true);
    state.form.symbolField = state.form.symbolField.setDisabled(true);
    state.form.sideField = state.form.sideField.setDisabled(true);
  }

  if (quote.FixingDetails && quote.PricingMode === PricingModeEnum.SpreadToFixing) {
    state.form.fixingField = FixingField.createFromQuote(quote).setIsRequired(true);
  }

  if (quote.ExternalGroup) {
    state.form.externalIDField = state.form.externalIDField.updateValue(quote.ExternalGroup);
  }
  if (quote.ExternalComments) {
    state.form.externalCommentsField = state.form.externalCommentsField.updateValue(quote.ExternalComments);
  }

  if (quote.SettleDate) {
    state.form.settleDateField = state.form.settleDateField.updateValue(quote.SettleDate);
  }

  validate(state);
};

const validateQuantity = (state: WritableDraft<RFQState>) => {
  state.form.quantityField = state.form.quantityField.validate(
    [numberIsPositive, quantityValidation, careOrderQuantityValidation],
    state
  );
};

const validate = (state: WritableDraft<RFQState>) => {
  validateQuantity(state);
  validateAllocations(state);
};

function getQuantityIncrement(security: Security | undefined, currency: RFQCurrency | undefined) {
  if (security == null || currency == null) {
    return 0;
  }

  if (currency === UNITS_CURRENCY) {
    return 0;
  }

  const increment = currency === security.BaseCurrency ? security.MinSizeIncrement : security.MinPriceIncrement;
  return getScaleFromIncrement(increment);
}

/** Gets new state object (resetting the form to an empty form). Keeps necessary pieces of reference data around. */
function getClearFormState(state: WritableDraft<RFQState>): WritableDraft<RFQState> {
  const newState = getInitialState();

  // Keep ref data around. Add more here in the future as necessary.
  newState.referenceData = state.referenceData;
  newState.dependencies = state.dependencies;

  return newState;
}

function applyDefaultAllocationStateOnPrime(state: WritableDraft<RFQState>, params: PrimeRFQParams) {
  if (params.subAccount) {
    // Easy, if a sub account is provided by the primer, always use that
    updateSubAccountOrAllocationState(state, { SubAccount: params.subAccount });
  } else {
    // Else, we need to decide how to default the sub account.
    if (params.defaultToEmptySubAccount) {
      // No sub account was provided by the primer, and they want us to not default. We do this, except for the case when there is only one SA in the system.
      if (state.dependencies.tradableSubAccounts.length === 1) {
        updateSubAccountOrAllocationState(state, {
          SubAccount: state.dependencies.tradableSubAccounts.at(0)?.Name,
        });
      } else {
        updateSubAccountOrAllocationState(state, { SubAccount: EMPTY_SUB_ACCOUNT_SELECTION });
      }
    } else {
      const shouldDefaultSubAccount = !state.form.allocations.length && state.referenceData.defaultSubAccount;
      if (shouldDefaultSubAccount) {
        updateSubAccountOrAllocationState(state, { SubAccount: state.referenceData.defaultSubAccount! });
      }
    }
  }
}

/** Looks at the settings of our state, and determines what RFQ Trading Protocol to default to */
function getDefaultRFQTradingProtocol(state: WritableDraft<RFQState>): RFQTradingProtocolEnum {
  const { enableNegotiatedRFQFlow, enableTakerMarketplaceETFRFQFlow, rfqFormTradingProtocol } =
    state.referenceData.settings;

  // Persisted protocol should only be honored when the protocol switch is available to the user. The switch is showed when this flag below is true.
  if (enableNegotiatedRFQFlow && rfqFormTradingProtocol != null) {
    return rfqFormTradingProtocol;
  }

  // Else, default based on the ETFRFQFlow flag this first time
  return enableTakerMarketplaceETFRFQFlow ? RFQTradingProtocolEnum.Negotiated : RFQTradingProtocolEnum.Streaming;
}

/** Given either a Quote or Order, will pick out the RFQ Trading Protocol to use. If we can't resolve one (shouldn't happen...), we default. */
function getRFQTradingProtocolFromEntity(
  state: WritableDraft<RFQState>,
  entity: Quote | Order
): RFQTradingProtocolEnum {
  if (entity instanceof Order) {
    if (entity.PricingMode === PricingModeEnum.SpreadToFixing) {
      // Orders do not have an RFQTradingProtocol field. SpreadToFixing implies Negotiated semantics.
      return RFQTradingProtocolEnum.Negotiated;
    }

    return getDefaultRFQTradingProtocol(state);
  }

  // entity is Quote here
  return entity.RFQTradingProtocol ?? getDefaultRFQTradingProtocol(state);
}

/** Call when updating the RFQ Trading Protocol. Also makes updates to related state fields that go along with trading protocol changes. */
function updateRFQTradingProtocol(
  state: WritableDraft<RFQState>,
  rfqTradingProtocol: RFQTradingProtocolEnum | undefined
) {
  state.form.rfqTradingProtocolField = state.form.rfqTradingProtocolField.updateValue(rfqTradingProtocol);
  syncSplitRFQStateWithTradingProtocol(state);

  const showSpreadToFixingFields =
    state.referenceData.settings.enableTakerMarketplaceETFRFQFlow &&
    rfqTradingProtocol === RFQTradingProtocolEnum.Negotiated;

  if (showSpreadToFixingFields) {
    // If we just flipped to Negotiated protocol, and we support the ETFRFQ flow, then we can set Spread to Fixing.
    // In this case, we need to make the pricing mode field visible, and default the fixing field, both of which become required.
    state.form.pricingModeField = state.form.pricingModeField
      .setIsVisible(true)
      .setDisabled(state.referenceData.settings.disableNonSTFNegotiatedRFQ)
      .updateValue(PricingModeEnum.SpreadToFixing); // default to StF

    const security = state.form.symbolField.value;
    if (security) {
      state.form.fixingField = FixingField.createFromSecurity(
        security,
        state.referenceData.fixingIndices.tradableIndicesBySymbol
      );
    }
    state.form.fixingField = state.form.fixingField.setIsRequired(true);
  } else {
    // Otherwise, pricing mode field and fixing field are no longer relevant to our current setting. Either because we're no longer Protocol = Negotiated,
    // or if we are Protocol=Negotiated but we don't support Spread-to-Fixing.
    state.form.pricingModeField = state.form.pricingModeField.setIsVisible(false).updateValue(undefined);
    state.form.fixingField = new FixingField(); // reset
  }
}

function canEnableSplitRFQs(rfqTradingProtocol: RFQTradingProtocolEnum | undefined): boolean {
  // Split RFQs - as in sending one separate QuoteRequest per selected Market - is only applicable when protocol is Negotiated.
  // Main reason is technical: we currently CANCEL any Streaming requests that are not active in the RFQ Form. The whole premise of Split
  // RFQs is that you create many RFQs at once, none of which appearing in the RFQ Form. Instead, they "live" in the RFQ Blotter.
  // Secondly, this is only needed for Negotiated currently, so keeping the scope down. But this reason isn't really real in terms of business reason
  return rfqTradingProtocol === RFQTradingProtocolEnum.Negotiated;
}

function syncSplitRFQStateWithTradingProtocol(state: WritableDraft<RFQState>): void {
  if (!canEnableSplitRFQs(state.form.rfqTradingProtocolField.value)) {
    state.form.splitRFQs = false;
  }
}

const handleSymbolChange = (state: WritableDraft<RFQState>, symbol: string | undefined) => {
  const security = symbol ? state.referenceData.securities.securitiesBySymbol.get(symbol) : undefined;
  if (!security) {
    state.form.sizeCountering = false;
    return;
  }

  state.form.symbolField = state.form.symbolField.updateValue(security);
  state.productGroup = getGroup(security);
  if (!securityHasSizeCounteringFeatureFlagEnabled(security, state.referenceData.settings)) {
    state.form.sizeCountering = false;
  }

  // Set the correct currency on the state
  getRFQFormCurrencySpec(security).onSymbolChanged(state, security);

  // Set up the quantity field based on the security and the currency we derived above
  state.form.quantityField = state.form.quantityField
    .updateValue(security.NormalSize, true)
    .updateScale(getQuantityIncrement(security, state.form.rfqCurrencyField.value));

  // If we have selected an Option product, we need to create the option model instance
  if (state.productGroup === 'Option') {
    createRFQOptionModel(state, security);
  }

  // On symbol change, we always try to default the sub account
  const shouldDefaultSubAccount = state.form.allocations.length === 0 && state.referenceData.defaultSubAccount;
  if (shouldDefaultSubAccount) {
    updateSubAccountOrAllocationState(state, { SubAccount: state.referenceData.defaultSubAccount! });
  }

  updateRFQTradingProtocol(state, getDefaultRFQTradingProtocol(state));
};

const rfqSlice = createSlice({
  name: 'RFQ',
  initialState: getInitialState(),
  reducers: {
    setReferenceData: (state, action: PayloadAction<OMSReferenceDataState>) => {
      state.referenceData = action.payload;
    },
    setDependencies: (state, action: PayloadAction<RFQDependencies>) => {
      state.dependencies = action.payload;

      if (!state.currentQuote) {
        if (state.form.allocations.length && action.payload.tradableSubAccounts?.length != null) {
          // Filter out sub-accounts that are no longer tradeable
          state.form.allocations = state.form.allocations.filter(a =>
            a.subAccountField.value
              ? action.payload.tradableSubAccounts!.find(sb => sb.Name === a.subAccountField.value)
              : true
          );
        }

        validateAllocations(state);
      }
    },

    resubmitRFQ: (state, action: PayloadAction<Quote | Order>) => {
      state = getClearFormState(state);
      if (isOrder(action.payload)) {
        initializeFieldsFromOrder(state, action.payload);
      } else {
        initializeFieldsFromQuote(state, action.payload);
      }

      return state;
    },
    showRFQ: (state, action: PayloadAction<Quote>) => {
      state = getClearFormState(state);
      initializeFieldsFromQuote(state, action.payload);

      state.currentQuote = action.payload;
      return state;
    },
    primeNewRFQForm: (state, action: PayloadAction<PrimeRFQParams>) => {
      state = getClearFormState(state);
      const parentID = action.payload.parent?.id;

      const symbol = action.payload.symbol;
      handleSymbolChange(state, symbol);

      state.form.sideField = state.form.sideField.updateValue(action.payload.side);
      state.form.rfqMarketsField = getInitialState().form.rfqMarketsField;

      // Differentiate between priming with empty initial quantity vs using security's NormalSize
      if (action.payload.orderQty != null) {
        state.form.quantityField = state.form.quantityField
          // removes trailing zeroes after the decimal place if we toFixed() a Big object instead of just setting the rawString
          .updateValue(action.payload.orderQty && toBigWithDefault(action.payload.orderQty, 0).toFixed());
      }

      if (action.payload.currency) {
        state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(action.payload.currency);
      }

      state.form.groupField = state.form.groupField.updateValue(action.payload.group);

      applyDefaultAllocationStateOnPrime(state, action.payload);

      if (action.payload.externalID) {
        state.form.externalIDField = state.form.externalIDField.updateValue(action.payload.externalID);
      }
      if (parentID != null || action.payload.externalComments) {
        state.form.externalCommentsField = state.form.externalCommentsField.updateValue(
          action.payload.externalComments
        );
      }
      if (action.payload.settleDate) {
        state.form.settleDateField = state.form.settleDateField.updateValue(action.payload.settleDate);
      }
      if (action.payload.rfqTradingProtocol != null) {
        // If a protocol is provided in the primer we always use it, regardless of stored user defaults.
        updateRFQTradingProtocol(state, action.payload.rfqTradingProtocol);
      }

      state.form.parentIDField = state.form.parentIDField.updateValue(parentID);
      state.form.parentTypeField = state.form.parentTypeField.updateValue(action.payload.parent?.type);
      state.form.assignToParentField = state.form.assignToParentField.updateValue(parentID != null);

      if (parentID != null) {
        // If we are primed from a parent, there are certain fields you are not allowed to change
        state.form.symbolField = state.form.symbolField.setDisabled(true);
        state.form.fundField = state.form.fundField.setDisabled(true);

        if (action.payload.group) {
          state.form.groupField = state.form.groupField.setDisabled(true);
        }

        // If we were primed with a sub account from a parent entity we are locked into this sub account
        if (action.payload.subAccount) {
          disableAllocationFields(state, true);
        }
      }

      validate(state);
      return state; // we create a new cleared state instance above, so we need to return it
    },
    setSide: (state, action: PayloadAction<SideEnum | undefined>) => {
      state.form.sideField = state.form.sideField.updateValue(action.payload);
      validate(state);
    },
    setFund: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload == null) {
        // fund was cleared. We don't need to clear any form state in this case, we can keep what is selected
        state.form.fundField = state.form.fundField.updateValue(undefined);
        return;
      }

      const fundSymbols = state.referenceData.funds.fundsBySymbol.get(action.payload)?.TradableSecurities;
      const currentSymbol = state.form.symbolField.value?.Symbol;
      const newFundChangesSymbol = !currentSymbol || !fundSymbols?.includes(currentSymbol);
      if (newFundChangesSymbol) {
        state = getClearFormState(state);
        state.form.fundField = state.form.fundField.updateValue(action.payload);
        handleSymbolChange(state, fundSymbols?.at(0));
      } else {
        state.form.fundField = state.form.fundField.updateValue(action.payload);
      }

      // In the newFundChangesSymbol code path we are changing the state reference, so need to return the new one
      return state;
    },
    setSymbol: (state, action: PayloadAction<string | undefined>) => {
      const sizeCountering = state.form.sizeCountering;
      state = getClearFormState(state);
      state.form.sizeCountering = sizeCountering;
      handleSymbolChange(state, action.payload);
      return state;
    },
    setProductGroup: (state, action: PayloadAction<ProductGroup>) => {
      const newState = getClearFormState(state); // wipe everything
      newState.productGroup = action.payload;
      if (action.payload === 'Option') {
        createRFQOptionModel(newState);
      }
      return newState;
    },
    updateOption: (state, action: PayloadAction<Option>) => {
      // Updating the option is just a proxy for changing the symbol. When changing the symbol, we always clear the state.
      // For options, it can be annoying for the user that we reset the form when they just change the strike... but for technical simplicity, this is how it is currently.
      const newState = getClearFormState(state);
      newState.productGroup = state.productGroup;
      newState.form.option = action.payload;

      // Symbol might not always be defined. The option model can be "half-built", eg just Exchange and Coin are provided, for example.
      const symbol = action.payload.security?.Symbol;
      if (symbol) {
        handleSymbolChange(newState, symbol);
      } else {
        newState.form.symbolField = newState.form.symbolField.updateValue(undefined);
      }

      return newState;
    },
    setQuantity: (state, action: PayloadAction<string>) => {
      state.form.quantityField = state.form.quantityField.updateValue(action.payload);
      validateQuantity(state);
    },
    toggleAssignToParent: state => {
      const nextAssignToParentValue = !state.form.assignToParentField.value;
      state.form.assignToParentField = state.form.assignToParentField.updateValue(nextAssignToParentValue);

      const currentCareOrder = state.currentCareOrder;
      if (nextAssignToParentValue === true) {
        const coSecurity = currentCareOrder?.Symbol
          ? state.referenceData.securities.securitiesBySymbol.get(currentCareOrder?.Symbol)
          : undefined;
        state.form.symbolField = state.form.symbolField.updateValue(coSecurity).setDisabled(true);
        if (state.referenceData.settings.enableRFQFormFundField) {
          state.form.fundField = state.form.fundField.updateValue(currentCareOrder?.fund).setDisabled(true);
        }
        state.form.sideField = state.form.sideField.updateValue(undefined); // Set back to 2-way

        state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(
          careOrderCurrencyToRFQSliceCurrency(currentCareOrder?.Currency)
        );

        // Conditional setting below... only set if the CO has the properties
        if (currentCareOrder?.Group) {
          state.form.groupField = state.form.groupField.updateValue(currentCareOrder.Group).setDisabled(true);
        }
        if (currentCareOrder?.SubAccount) {
          updateSubAccountOrAllocationState(state, { SubAccount: currentCareOrder.SubAccount });
          disableAllocationFields(state, true);
        }
      } else {
        // Unchecked, we need to revert some of the things we did above, mostly just re-enabling fields
        state.form.symbolField = state.form.symbolField.setDisabled(false);
        state.form.fundField = state.form.fundField.setDisabled(false);

        // Conditional resetting below
        if (currentCareOrder?.Group) {
          state.form.groupField = state.form.groupField.updateValue('').setDisabled(false);
        }
        if (currentCareOrder?.SubAccount) {
          // Keep selection, but set back to enabled to let the user change allocs
          disableAllocationFields(state, false);
        }
      }

      validate(state);
    },
    setRFQCurrency: (state, action: PayloadAction<RFQCurrency>) => {
      state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(action.payload);
      state.form.quantityField = state.form.quantityField
        .updateValue('', true)
        .updateScale(getQuantityIncrement(state.form.symbolField.value, action.payload));
      validateQuantity(state);
    },
    setCurrentQuote: (state, { payload: quote }: PayloadAction<Quote>) => {
      state.quoteRequested = false;

      // If we've reached a terminal state, go back to showing the form as it was
      if (quote.isTerminal) {
        state.isLoading = false;
        state.currentQuote = undefined;
        return;
      }

      state.isLoading = quote.isPending;
      state.currentQuote = quote;
    },
    setCurrentCareOrder: (state, { payload: co }: PayloadAction<CareOrder | undefined>) => {
      // Called when the Care Order that is our parent order comes in
      state.currentCareOrder = co;

      if (state.currentCareOrder?.isComplete) {
        state.form.assignToParentField = state.form.assignToParentField.updateValue(false);
      }

      if (
        state.referenceData.settings.enableRFQFormFundField &&
        co?.fund &&
        state.form.assignToParentField.value === true
      ) {
        // Fund does not exist on the RFQ itself, or as an input from the priming. When we have a parent CO,
        // the fund comes from the CO only. So when we receive our care order we are related to, if we are assigned to it, set this fund.
        state.form.fundField = state.form.fundField.updateValue(co.fund);
      }
      validateQuantity(state);
    },
    setAllocations: (state, action: PayloadAction<Allocation[]>) => {
      state.form.allocations = mapAllocationsToField(action.payload, state.form.allocationValueTypeField.value, true);
      validateAllocations(state);

      const resetOrderAccountsOnSubAccountChange =
        state.referenceData.settings.enableAccountSegregation && // only do this if trading acct segregation is enabled
        state.referenceData.settings.resetOrderAccountsOnSubAccountChange;

      // we only do our resetting logic if we are just working with 1 sub account, and not several. Doing < 2 also catches the case where
      // we go from 0->1 and 1->0, since those are valid "reset" state transitions as well.
      const isSingleSubAccountChange = state.form.allocations.length < 2 && action.payload.length < 2;
      if (resetOrderAccountsOnSubAccountChange && isSingleSubAccountChange) {
        state.form.rfqMarketsField = getInitialState().form.rfqMarketsField; // reset
      }
    },
    setAllocationValueType: (state, action: PayloadAction<AllocationValueTypeEnum>) => {
      const mappedToNewUnit = state.form.allocations.map(pair => ({
        subAccountField: pair.subAccountField,
        valueField: pair.valueField
          .updateUnit(action.payload === AllocationValueTypeEnum.Percentage ? Unit.Percent : Unit.Decimal)
          .updateValue(
            state.form.allocationValueTypeField.value === AllocationValueTypeEnum.Percentage
              ? pair.valueField.displayValue
              : pair.valueField.value
          ),
      }));
      state.form.allocations = mappedToNewUnit;
      state.form.allocationValueTypeField = state.form.allocationValueTypeField.updateValue(action.payload);
      validateAllocations(state);
    },
    setRFQMarkets: (state, action: PayloadAction<{ markets: string[]; aggregation?: string }>) => {
      const newValue: MarketSelectorValue = action.payload.aggregation
        ? { type: 'preset', presetID: action.payload.aggregation, selections: action.payload.markets }
        : { type: 'custom', selections: action.payload.markets };

      state.form.rfqMarketsField = state.form.rfqMarketsField.updateValue(newValue);
    },
    setPricingMode: (state, { payload: pricingMode }: PayloadAction<PricingModeEnum | undefined>) => {
      handlePricingModeChange(state, pricingMode);
    },
    setRFQTradingProtocol: (
      state,
      { payload: rfqTradingProtocol }: PayloadAction<RFQTradingProtocolEnum | undefined>
    ) => {
      state.form.rfqMarketsField = getInitialState().form.rfqMarketsField;
      updateRFQTradingProtocol(state, rfqTradingProtocol);
    },
    initializeRFQMarketsField: (
      state,
      action: PayloadAction<{
        desiredValue: MarketSelectorValue | undefined;
        items: MarketSelectorItem[];
        /** Provide directly to override the default seting held in the redux store */
        defaultToEmptyMarkets?: boolean;
      }>
    ) => {
      state.form.rfqMarketsField = state.form.rfqMarketsField.initialize({
        desiredValue: action.payload.desiredValue,
        items: action.payload.items,
        settings: {
          defaultToEmpty:
            action.payload.defaultToEmptyMarkets ?? state.referenceData.settings.defaultToEmptyOrderMarkets,
          defaultMarketAccounts: state.referenceData.settings.defaultMarketAccounts,
          defaultAggregationId: state.referenceData.settings.defaultAggregationId,
          aggregationPresets: state.referenceData.tradingAggregationPresets,
        },
      });
    },

    handleRFQMarketSelectorItemsChange: (state, action: PayloadAction<MarketSelectorItem[]>) => {
      state.form.rfqMarketsField = state.form.rfqMarketsField.updateItems(action.payload);
    },
    setGroup: (state, action: PayloadAction<string | undefined>) => {
      state.form.groupField = state.form.groupField.updateValue(action.payload);
    },
    setFixingIndex: (state, action: PayloadAction<{ symbol: string; index: string | undefined }>) => {
      state.form.fixingField = state.form.fixingField.setFixing(action.payload.symbol, action.payload.index);
    },
    setExternalID: (state, action: PayloadAction<string | undefined>) => {
      state.form.externalIDField = state.form.externalIDField.updateValue(action.payload);
    },
    setExternalComments: (state, action: PayloadAction<string | undefined>) => {
      state.form.externalCommentsField = state.form.externalCommentsField.updateValue(action.payload);
    },
    setSettleDate: (state, action: PayloadAction<string | undefined>) => {
      state.form.settleDateField = state.form.settleDateField.updateValue(action.payload);
    },
    setQuoteRequested: state => {
      state.isLoading = true;
      state.quoteRequested = true;
    },
    setSplitRFQs: (state, action: PayloadAction<boolean>) => {
      state.form.splitRFQs = action.payload;
    },
    setSizeCounteringEnabled: (state, action: PayloadAction<boolean>) => {
      state.form.sizeCountering = action.payload;
    },
    setPartialFillsEnabled: (state, action: PayloadAction<boolean>) => {
      state.form.partialFills = action.payload;
    },
    // Pretty much just the inverse of what we do directly above in the setQuoteRequested action
    onQuoteRequestFailed: state => {
      state.isLoading = false;
      state.quoteRequested = false;
    },
    onNewOrderSingleSent: state => {
      // NOS is sent when accepting a quote
      state.isLoading = true;
    },
    onNewOrderSingleFailed: state => {
      state.isLoading = false;
    },
    /**
     * The various entry point thunks to the RFQ Slice (priming, resubmitting, onMount) all run this action after completion to let others know that initialization is done
     * Currently the only thing we actually initialize is the markets field
     */
    setInitialized: state => {
      state.initialized = true;
    },
    onUnMount: state => {
      // This action is called whenever the RFQ form is closed. The user has not clicked Clear, they have just left the form for some other reason.
      // When adding new fields to this form, you should always add it into this action in one way or another.
      const newState: WritableDraft<RFQState> = getClearFormState(state);

      // We choose to not persist any state if our state is tied to a parent entity (eg a Care Order). This is just for simplicity.
      const shouldPersist = state.form.parentIDField.value == null;

      if (shouldPersist) {
        // Persist all these fields for when they open the form next
        newState.form.fundField = state.form.fundField.setTouched(false);
        newState.form.symbolField = state.form.symbolField.setTouched(false);
        newState.productGroup = state.productGroup;
        newState.form.fixingField = state.form.fixingField.setTouched(false);
        newState.form.sideField = state.form.sideField.setTouched(false);
        newState.form.quantityField = state.form.quantityField.setTouched(false);
        newState.form.rfqCurrencyField = state.form.rfqCurrencyField.setTouched(false);
        newState.form.rfqMarketsField = state.form.rfqMarketsField.setTouched(false);
        newState.form.groupField = state.form.groupField.setTouched(false);
        newState.form.allocationValueTypeField = state.form.allocationValueTypeField.setTouched(false);
        newState.form.allocations = state.form.allocations.map(alloc => ({
          subAccountField: alloc.subAccountField.setTouched(false),
          valueField: alloc.valueField.setTouched(false),
        }));
        newState.form.rfqTradingProtocolField = state.form.rfqTradingProtocolField.setTouched(false);
        newState.form.pricingModeField = state.form.pricingModeField.setTouched(false);
        newState.form.externalIDField = state.form.externalIDField.setTouched(false);
        newState.form.externalCommentsField = state.form.externalCommentsField.setTouched(false);
        newState.form.settleDateField = state.form.settleDateField.setTouched(false);
        newState.form.splitRFQs = state.form.splitRFQs;
        newState.form.sizeCountering = state.form.sizeCountering;
        newState.form.partialFills = state.form.partialFills;
        newState.form.option = state.form.option;
      }

      return newState;
    },
    // different to onUnMount, this is called when the user specifically clicks the "Clear" button. It resets state.
    clear: state => {
      return getClearFormState(state);
    },
    touchAll: state => {
      getTypedKeys(state.form).forEach(key => {
        if (state.form[key] instanceof BaseField) {
          // @ts-expect-error - BaseField methods have trouble lining up on keyed calls
          state.form[key] = state.form[key].setTouched(true);
        }
      });

      touchAllocations(state, true);
    },
  },
});

export const {
  setReferenceData,
  setDependencies,
  setSide,
  setQuantity,
  setAllocationValueType,
  setPricingMode,
  setGroup,
  setFixingIndex,
  setExternalID,
  setExternalComments,
  setSettleDate,
  setRFQCurrency,
  showRFQ,
  setCurrentQuote,
  toggleAssignToParent,
  setQuoteRequested,
  setSplitRFQs,
  setSizeCounteringEnabled,
  setPartialFillsEnabled,
  onNewOrderSingleSent,
  onNewOrderSingleFailed,
  onQuoteRequestFailed,
  onUnMount,
  clear,
  setRFQMarkets,
  setProductGroup,
  handleRFQMarketSelectorItemsChange,
  touchAll,
} = rfqSlice.actions;

/**
 * The referenceData populated in all our slices is app-wide, so it includes all options
 * For RFQ, we can only trade certain options that exist on certain markets. For now, we hard-code a check on "falconx".
 */
function getRFQOptionModelRefData(input: OMSReferenceDataState): OptionModelReferenceData {
  const rfqOptions = input.options.options.filter(isFalconXOption);

  return {
    options: {
      options: rfqOptions,
      strikesByOptionKey: getStrikesByOptionKey(rfqOptions),
      expirationByMarketByCurrencyIdentity: getExpirationByMarketByCurrencyIdentity(rfqOptions),
    },
    markets: {
      marketsByName: input.markets.marketsByName,
    },
    marketAccounts: {
      marketAccountsByMarket: input.marketAccounts.marketAccountsByMarket,
    },
  };
}

/** Wraps the Option model creation in some RFQ-specific logic to ensure creation is done with the correct inputs */
function createRFQOptionModel(state: WritableDraft<RFQState>, security?: Security) {
  const optionModelRefData = getRFQOptionModelRefData(state.referenceData);

  if (security && SecurityTypesUtils.Option.isType(security)) {
    state.form.option = Option.createFromSecurity(optionModelRefData, security);
  } else {
    state.form.option = Option.createFromBlank(optionModelRefData);
  }
}

const initializeRFQMarketsFieldThunk = getInitMarketsThunk({
  sliceID: 'RFQSlice',
  selectCanInit: rfqSelectors.selectCanInitMarketsField,
  selectMSSRequestParams: rfqSelectors.selectMSSRequestParams,
  buildItems: buildMarketSelectorItems,
  initMarketsField: rfqSlice.actions.initializeRFQMarketsField,
});

const primeNewRFQFormThunk = createAsyncThunk<void, PrimeRFQParams, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/primeNewRFQFormThunk',
  (params, thunk) => {
    thunk.dispatch(rfqSlice.actions.primeNewRFQForm(params));

    // If the rfq markets field was reset after the dispatch directly above, we start the initialization of market selector items
    if (!thunk.getState().rfq.form.rfqMarketsField.initialized) {
      let desiredMarketSelectorValue: MarketSelectorValue | undefined = undefined;
      if (params.marketAccounts) {
        desiredMarketSelectorValue = params.aggregation
          ? { type: 'preset', presetID: params.aggregation, selections: params.marketAccounts }
          : { type: 'custom', selections: params.marketAccounts };
      }

      thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: desiredMarketSelectorValue }));
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);
export { primeNewRFQFormThunk as primeNewRFQForm };

/**
 * Mounting is a special thunk. It is one of a handful of entry points into the RFQ Form. It is the only entry point thunk which can be
 * called _after_ someone else has already initialized. This is what differentiates it from prime, modify, resubmit. Because of this,
 * onMountThunk needs to be aware of the rfq.initialized state.
 *
 * onMountThunk is responsible for making sure that we resume operation correctly if the user closes and just re-opens the form
 * where state is persisted. For example, we need to re-kick off the Markets Field with its async listeners.
 */
const onMountThunk = createAsyncThunk<void, void, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/onMountThunk',
  (_, thunk) => {
    // If we already have a symbol set, but nobody has kicked off initialization yet, then we need to perform an init steps.
    // This happens in the RFQ Form and not the Order Form because the RFQ Form remembers your selected Symbol despite closing.
    if (thunk.getState().rfq.form.symbolField.value != null && !thunk.getState().rfq.initialized) {
      // The Markets field is a persisted property. If something was set, we try to re-init to it (things might've changed tho)
      thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: thunk.getState().rfq.form.rfqMarketsField.value }));
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);
export { onMountThunk as onMount };

const setSymbolThunk = createAsyncThunk<void, string | undefined, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/setSymbolThunk',
  (symbol, thunk) => {
    thunk.dispatch(rfqSlice.actions.setSymbol(symbol));

    // If the rfq markets field was reset after the dispatch directly above, we start the initialization of market selector items
    if (!thunk.getState().rfq.form.rfqMarketsField.initialized) {
      thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: undefined }));
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);
export { setSymbolThunk as setSymbol };

const resubmitRFQThunk = createAsyncThunk<void, Quote | Order, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/resubmitRFQThunk',
  (quoteOrOrder, thunk) => {
    thunk.dispatch(rfqSlice.actions.resubmitRFQ(quoteOrOrder));

    const tradedMarketAccounts =
      quoteOrOrder instanceof Order
        ? compact(
            quoteOrOrder.Markets.filter(m => m.MarketStatus !== OrderMarketStatusEnum.Disabled).map(
              m => m.MarketAccount
            )
          )
        : // Quote.Markets don't have a MarketStatus it seems
          compact(quoteOrOrder.Markets.map(m => m.MarketAccount));

    thunk.dispatch(
      initializeRFQMarketsFieldThunk({ desiredValue: { type: 'custom', selections: tradedMarketAccounts } })
    );

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);
export { resubmitRFQThunk as resubmitRFQ };

const setAllocationsThunk = createAsyncThunk<void, Allocation[], { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/setAllocationsThunk',
  (params, thunk) => {
    thunk.dispatch(rfqSlice.actions.setAllocations(params));

    if (!thunk.getState().rfq.form.rfqMarketsField.initialized) {
      // If the setAllocations action cleared the rfqMarketsField, then we kick off a initialization thunk here
      thunk.dispatch(
        // Override default to empty to be false for this action
        initializeRFQMarketsFieldThunk({ desiredValue: undefined, defaultToEmptyMarkets: false })
      );
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);
export { setAllocationsThunk as setAllocations };

const setRFQTradingProtocolThunk = createAsyncThunk<
  void,
  RFQTradingProtocolEnum | undefined,
  { state: AppState; extra: AppExtraArgument }
>('rfqSlice/setRFQTradingProtocolThunk', (params, thunk) => {
  thunk.dispatch(rfqSlice.actions.setRFQTradingProtocol(params));

  if (!thunk.getState().rfq.form.rfqMarketsField.initialized) {
    // If the setRFQTradingProtocol action cleared the rfqMarketsField, then we kick off a initialization thunk here
    thunk.dispatch(
      // Override default to empty to be false for this action
      initializeRFQMarketsFieldThunk({ desiredValue: undefined, defaultToEmptyMarkets: false })
    );
  }

  thunk.dispatch(rfqSlice.actions.setInitialized());
});
export { setRFQTradingProtocolThunk as setRFQTradingProtocol };

/**
 * UpdateOption is kind of like a proxy for setSymbol. Due to this, we need to treat it like an entry point to the RFQ Slice,
 * and make sure that we instantiate the slice if necessary ("instantiation" = init the markets field)
 */
const updateOptionThunk = createAsyncThunk<void, Option, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/updateOptionThunk',
  (option, thunk) => {
    thunk.dispatch(rfqSlice.actions.updateOption(option));

    if (!thunk.getState().rfq.form.rfqMarketsField.initialized) {
      thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: undefined }));
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);
export { updateOptionThunk as updateOption };

const setFundThunk = createAsyncThunk<void, string | undefined, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/setFundThunk',
  (fund, thunk) => {
    thunk.dispatch(rfqSlice.actions.setFund(fund));

    // Setting the fund might trigger a change in symbol and a form reset, which might lead to needing to re-init here
    if (!thunk.getState().rfq.form.rfqMarketsField.initialized) {
      thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: undefined }));
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);
export { setFundThunk as setFund };

function buildMarketSelectorItems(
  state: AppState,
  onDemandState: { mss: Map<string, Map<string, MarketSecurityStatus>> | undefined }
): MarketSelectorItem[] {
  const params = rfqSelectors.selectBuildRFQMarketSelectorItemsParams(state);
  const items = getRFQMarketSelectorItems({
    ...params,
    mssByAccountBySymbol: onDemandState.mss,
    enableMSSValidation: state.referenceData.settings.enableMSSValidation,
  });

  return items;
}

function getParentOrderEndpoint(parentType: RFQParentType | undefined) {
  return parentType === 'working-order'
    ? streamingDataSlice.endpoints.getWorkingOrders
    : streamingDataSlice.endpoints.getCareOrders;
}

export function setupListeners(startListening: AppStateListenerStart) {
  /**
   * Subscribe (via RTK query) to the care order with the given OrderID,
   * so we can update the form if the care order changes.
   */
  const unsubscribeCareOrderForModify = startListening({
    matcher: action =>
      rfqSlice.actions.primeNewRFQForm.match(action) ||
      rfqSlice.actions.resubmitRFQ.match(action) ||
      showRFQ.match(action),
    effect: async (_, listener) => {
      listener.cancelActiveListeners();

      const state = listener.getState();

      const orderID = state.rfq.form.parentIDField.value;
      const queryArg = { OrderID: orderID };
      if (orderID == null) {
        return;
      }

      const parentType = state.rfq.form.parentTypeField.value;
      const parentOrderEndpoint = getParentOrderEndpoint(parentType);
      const selectCareOrder = parentOrderEndpoint.select(queryArg);
      // Check if there's already a cached value for this OrderID
      const careOrder = selectCareOrder(listener.getState()).data?.get(orderID);
      if (careOrder != null) {
        listener.dispatch(rfqSlice.actions.setCurrentCareOrder(careOrder));
      }

      // Listen for changes to the care order
      const unsubscribeCareOrderForModifyListener = startListening({
        predicate: (_, next, prev) => selectCareOrder(next) !== selectCareOrder(prev),
        effect: (_, listener) => {
          const careOrder = selectCareOrder(listener.getState()).data?.get(orderID);
          if (careOrder == null) {
            return;
          }
          listener.dispatch(rfqSlice.actions.setCurrentCareOrder(careOrder));
        },
      });
      const careOrders = listener.dispatch(parentOrderEndpoint.initiate(queryArg));

      // If this effect gets cancelled (e.g. the user closes the form), we should unsubscribe from the care order query
      listener.signal.addEventListener('abort', () => {
        careOrders.unsubscribe();
        unsubscribeCareOrderForModifyListener();
      });

      const prev = listener.getState();
      await listener.condition(() => {
        const next = listener.getState();
        const didCloseForm = next.OMS.openedView !== OMSView.RFQForm;
        const didChangeParentID = next.rfq.form.parentIDField.value !== prev.rfq.form.parentIDField.value;
        return didCloseForm || didChangeParentID;
      });
    },
  });

  const unsubscribeMarketSelectorItemsListener = startListening(
    getMarketSelectorItemsListener({
      id: 'rfqslice',
      predicate: action => initializeRFQMarketsFieldThunk.fulfilled.match(action),
      selectMSSRequestParams: rfqSelectors.selectMSSRequestParams,
      startListening,
      selectItemBuildingParams: rfqSelectors.selectBuildRFQMarketSelectorItemsParams,
      buildItems: buildMarketSelectorItems,
      onItemChange: (items, dispatch) => dispatch(rfqSlice.actions.handleRFQMarketSelectorItemsChange(items)),
      OMSView: OMSView.RFQForm,
    })
  );

  const unsubscribePositionSubListener = startListening({
    predicate: (_, next, prev) => {
      // We run the effect if the OMS was just opened to RFQForm.
      return prev.OMS.openedView !== next.OMS.openedView && next.OMS.openedView === OMSView.RFQForm;
    },
    effect: async (_, listener) => {
      /**
       * This effect will kick off the Positions subscription which is later used in various RFQSlice selectors upon opening the RFQForm.
       * The listener will close the subscription when the user leaves the RFQForm.
       */
      listener.cancelActiveListeners();
      const positionSub = listener.dispatch(
        streamingDataSlice.endpoints.getPositions.initiate(RFQSLICE_POSITION_REQUEST_PARAMS)
      );

      listener.signal.addEventListener('abort', () => {
        positionSub.unsubscribe();
      });

      // Keep the listener alive until the order form is closed.
      await listener.condition(() => {
        return listener.getState().OMS.openedView !== OMSView.RFQForm;
      });
    },
  });

  return () => {
    unsubscribeCareOrderForModify();
    unsubscribeMarketSelectorItemsListener();
    unsubscribePositionSubListener();
  };
}
export const rfqSliceReducer = rfqSlice.reducer;
